import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import SEO from "../components/SEO";
import { servicesData } from "../staticData";
import { 
  ShieldCheck, 
  Sparkles, 
  Palette, 
  FileText,
  CheckCircle,
  ArrowRight,
  Star,
  Clock,
  DollarSign
} from "lucide-react";

const Services = () => {
  // Use static data instead of API
  const loading = false;
  const error = null;

  const iconMap = {
    "shield-check": ShieldCheck,
    "sparkles": Sparkles,
    "palette": Palette,
    "file-text": FileText
  };
  return (
    <div className="min-h-screen">
      <SEO 
        title="Our Services - WOW AUTOBODY | Collision Repair, Dent Removal, Auto Painting"
        description="Professional auto body services including collision repair, paintless dent removal, auto painting, and insurance claim assistance. Quality work with lifetime warranty."
        keywords="collision repair services, dent removal, auto painting, insurance claim repair, auto body services, car repair services"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="bg-red-600 text-white mb-6 px-4 py-2">
            Professional Auto Body Services
          </Badge>
          <h1 className="text-5xl font-bold mb-6">Our Services</h1>
          <p className="text-xl leading-relaxed">
            From minor touch-ups to major collision repairs, our certified technicians 
            provide comprehensive auto body services with precision and care.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {servicesData && servicesData.map((service) => {
              const IconComponent = iconMap[service.icon] || ShieldCheck;
              return (
                <Card key={service.id} className="p-8 border-2 hover:border-blue-200 hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-0">
                    <div className="flex items-start space-x-6">
                      <div className="bg-blue-100 p-4 rounded-xl">
                        <IconComponent className="h-8 w-8 text-blue-900" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                        <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                        <ul className="space-y-3 mb-6">
                          {service.features && service.features.map((feature, index) => (
                            <li key={index} className="flex items-center text-gray-700">
                              <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                        {service.price_range && (
                          <p className="text-lg font-semibold text-blue-900 mb-4">
                            Price: {service.price_range}
                          </p>
                        )}
                        <Link to="/contact">
                          <Button className="bg-red-600 hover:bg-red-700 text-white">
                            Get Quote
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Repair Process</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We follow a systematic approach to ensure the highest quality results for every repair.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: "1",
                title: "Initial Assessment",
                description: "Comprehensive damage evaluation and detailed estimate preparation",
                icon: FileText
              },
              {
                step: "2",
                title: "Insurance Coordination",
                description: "We handle all insurance paperwork and claim processing for you",
                icon: ShieldCheck
              },
              {
                step: "3", 
                title: "Expert Repair",
                description: "Skilled technicians restore your vehicle using premium materials",
                icon: Sparkles
              },
              {
                step: "4",
                title: "Quality Inspection", 
                description: "Thorough quality check and final detailing before delivery",
                icon: CheckCircle
              }
            ].map((process, index) => {
              const IconComponent = process.icon;
              return (
                <div key={index} className="text-center">
                  <div className="relative mb-6">
                    <div className="bg-blue-900 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                      {process.step}
                    </div>
                    <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto -mt-8 mb-4">
                      <IconComponent className="h-6 w-6 text-blue-900" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{process.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{process.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose WOW AUTOBODY?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We're committed to exceeding your expectations with every repair.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Star,
                title: "Premium Quality",
                description: "We use only OEM parts and premium materials for lasting repairs that maintain your vehicle's value.",
                color: "text-yellow-500"
              },
              {
                icon: Clock,
                title: "Fast Turnaround",
                description: "Most repairs completed within reasonable window. We provide rental car assistance when needed.",
                color: "text-blue-500"
              },
              {
                icon: DollarSign,
                title: "Fair Pricing",
                description: "Transparent, competitive pricing with detailed estimates. No hidden fees or surprise charges.",
                color: "text-green-500"
              }
            ].map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <Card key={index} className="p-8 text-center border-2 hover:border-blue-200 hover:shadow-lg transition-all">
                  <CardContent className="p-0">
                    <div className={`inline-flex p-4 rounded-full bg-gray-100 mb-6`}>
                      <IconComponent className={`h-8 w-8 ${benefit.color}`} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 opacity-90">
            Contact us today for a free estimate on any of our services. 
            We're here to help restore your vehicle to its best condition.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold">
                Get Free Estimate
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/gallery">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg font-semibold">
                View Our Work
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;